import { useEffect, useState } from 'react';
import { generatedIdOverlayCopy } from '../domain/identity/generatedIdOverlayCopy';

const STORAGE_KEY = 'sms.generatedIdOverlay.minimized';

/**
 * Sticky bottom-right guide for generated Student ID and Unix username.
 * Presentation only — copy lives in generatedIdOverlayCopy.
 */
const GeneratedIdGuideOverlay = ({ studentId = '', unixUsername = '', selection } = {}) => {
  const copy = generatedIdOverlayCopy({ studentId, unixUsername, selection });
  const [minimized, setMinimized] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored === '0') return false;
      if (stored === '1') return true;
    } catch {
      /* ignore */
    }
    // Default minimized so the first screen stays focused on required fields.
    return true;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, minimized ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [minimized]);

  if (minimized) {
    return (
      <aside
        aria-label={copy.title}
        className="fixed z-40 rounded-lg shadow-sm"
        style={{
          bottom: '1rem',
          right: '1rem',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-text)',
        }}
      >
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="flex items-center gap-2 px-3 py-2 text-xs font-medium"
          title="Expand generated IDs"
        >
          <span className="font-mono truncate max-w-[12rem]">
            {copy.studentId || 'Suggested ID'}
          </span>
          <span style={{ color: 'var(--color-text-secondary)' }}>▸</span>
        </button>
      </aside>
    );
  }

  return (
    <aside
      aria-label={copy.title}
      className="fixed z-40 max-h-[min(55vh,22rem)] w-[min(20rem,calc(100vw-1.5rem))] overflow-auto rounded-lg p-3 text-xs"
      style={{
        bottom: '1rem',
        right: '1rem',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        color: 'var(--color-text)',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          {copy.title}
        </p>
        <button
          type="button"
          onClick={() => setMinimized(true)}
          className="shrink-0 px-2 py-0.5 rounded text-xs"
          style={{
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)',
          }}
          title="Minimize"
        >
          Minimize
        </button>
      </div>
      <dl className="space-y-1 mb-2">
        <div>
          <dt className="inline font-medium">Student ID: </dt>
          <dd className="inline font-mono break-all">
            {copy.studentId || 'Fill program, mode, and center'}
          </dd>
        </div>
        {copy.unixUsername ? (
          <div>
            <dt className="inline font-medium">Unix username: </dt>
            <dd className="inline font-mono break-all">{copy.unixUsername}</dd>
          </div>
        ) : null}
      </dl>
      <dl className="space-y-1 mb-2" style={{ color: 'var(--color-text-secondary)' }}>
        {copy.details.map((row) => (
          <div key={row.label}>
            <dt className="inline font-medium">{row.label}: </dt>
            <dd className="inline break-all" style={{ color: 'var(--color-text)' }}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      <ul className="list-disc pl-4 space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
        {copy.notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </aside>
  );
};

export default GeneratedIdGuideOverlay;
